<template>
  <timeline
    data-sb-automation="timeline"
    data-sb-regression="sb-timeline"
    :class="{
      [$style.progressive]: progressive,
      [$style[variation || 'secondary']]: !progressive,
    }"
    :value="breakpointsData"
    :layout="layout"
  >
    <template #content="slotProps">
      <slot
        v-if="!isEmpty(slotProps.item)"
        name="content"
        v-bind="slotProps ?? {}"
        :is-active="isActive(slotProps.index)"
      />
    </template>
    <template #marker="slotProps">
      <slot
        name="marker"
        v-bind="slotProps ?? {}"
        :is-active="isActive(slotProps.index)"
      />
    </template>
    <template #connector="slotProps">
      <progress-connector
        v-if="progressive && !hasConnectorSlot"
        :is-left-rounded="slotProps.index === 0"
        :is-right-rounded="slotProps.index === breakpoints.length - 1"
        :filled="isActive(slotProps.index)"
        :is-last-selected="currentStepIndex === slotProps.index"
        :partial-filled="isPartialFilled(slotProps.index)"
        :layout="layout"
        :variation="variation || 'secondary'"
      />
      <slot
        v-else
        name="connector"
        v-bind="slotProps ?? {}"
        :is-active="isActive(slotProps.index)"
      />
    </template>
  </timeline>
</template>

<script>
import Timeline from 'primevue/timeline';
import ProgressConnector from '@src/components/Timeline/components/ProgressConnector.vue';

export default {
  name: 'NextTimeline',
  components: {
    Timeline,
    ProgressConnector,
  },
  props: {
    /**
       * A list of points to be iterated over for the scoped slot `SbProgressBarBreakPoint`
       */
    breakpoints: {
      type: Array,
      required: true,
      default: () => ([]),
    },

    /**
       * The current active step for the Timeline. Should be one of the breakpoints.
       */
    currentStep: {
      default: null,
    },

    /**
       * Function run to compare equality of the currentStep to one of the breakpoints. `(breakpoint, currentStep) => {}`
       */
    compareFunc: {
      type: Function,
      default: null,
    },
    /**
       * Style variation to give additional meaning.
       * `primary, error, warning, secondary, disabled, success, monochrome`
       */
    variation: {
      type: String,
      default: null,
      validator: (value) => {
        return value.match(/(primary|error|warning|success|disabled|monochrome|secondary)/i);
      },
    },

    /**
       * Flag to render a progressive-kind timeline with progress indicator
       */
    progressive: {
      type: Boolean,
      default: false,
    },

    /**
       * Layout type: 'vertical' or 'horizontal'. Vertical as default.
       */
    layout: {
      type: String,
      default: 'vertical',
      validator: (value) => {
        return value.match(/(vertical|horizontal)/i);
      },
    },
  },
  computed: {
    hasConnectorSlot() {
      return this.$slots.connector && this.$slots.connector();
    },
    breakpointsData() {
      if (this.progressive) return [...this.breakpoints, {}];
      return this.breakpoints;
    },
    currentStepIndex() {
      if (!this.compareFunc) return Number.MAX_VALUE;
      return this.breakpoints?.findIndex(
        (breakpointParam) => this.compareFunc(breakpointParam, this.currentStep),
      ) ?? -1;
    },
  },
  methods: {
    isActive(index) {
      return index <= this.currentStepIndex;
    },
    isEmpty(object) {
      return Object.keys(object || {}).length === 0;
    },
    isLastActive(index) {
      if (!this.compareFunc) return false;
      return this.breakpoints.findIndex(
        (breakpointParam) => this.compareFunc(breakpointParam, this.currentStep),
      ) === index;
    },
    isPartialFilled(index) {
      const ln = this.breakpoints?.length || 0;
      return index > 0 && index < ln - 1 && this.isLastActive(index);
    },
    getInactiveClass(context) {
      const { index } = context;
      return { inactive: !this.isActive(index), isLastActive: this.isLastActive(index) };
    },
  },
};
</script>

<style lang="scss" module>
.progressive {
  :global(.p-timeline-event-marker) {
    width: var(--p-timeline-event-progressive-marker-size);
    height: var(--p-timeline-event-progressive-marker-size);
    border-style: var(--p-timeline-event-progressive-marker-border-style);
  }
}

.primary {
  :global(.p-timeline-event-marker) {
    background: var(--p-timeline-color-primary-marker-background);
    border-color: var(--p-timeline-color-primary-marker-border);
  }

  :global(.p-timeline-event-connector) {
    background: var(--p-timeline-color-primary-connector-color);
  }
}

.error {
  :global(.p-timeline-event-marker) {
    background: var(--p-timeline-color-error-marker-background);
    border-color: var(--p-timeline-color-error-marker-border);
  }

  :global(.p-timeline-event-connector) {
    background: var(--p-timeline-color-error-connector-color);
  }
}

.warning {
  :global(.p-timeline-event-marker) {
    background: var(--p-timeline-color-warning-marker-background);
    border-color: var(--p-timeline-color-warning-marker-border);
  }

  :global(.p-timeline-event-connector) {
    background: var(--p-timeline-color-warning-connector-color);
  }
}

.success {
  :global(.p-timeline-event-marker) {
    background: var(--p-timeline-color-success-marker-background);
    border-color: var(--p-timeline-color-success-marker-border);
  }

  :global(.p-timeline-event-connector) {
    background: var(--p-timeline-color-success-connector-color);
  }
}

.disabled {
  :global(.p-timeline-event-marker) {
    background: var(--p-timeline-color-disabled-marker-background);
    border-color: var(--p-timeline-color-disabled-marker-border);
  }

  :global(.p-timeline-event-connector) {
    background: var(--p-timeline-color-disabled-connector-color);
  }
}

.monochrome {
  :global(.p-timeline-event-marker) {
    background: var(--p-timeline-color-monochrome-marker-background);
    border-color: var(--p-timeline-color-monochrome-marker-border);
  }

  :global(.p-timeline-event-connector) {
    background: var(--p-timeline-color-monochrome-connector-color);
  }
}

.secondary {
  :global(.p-timeline-event-marker) {
    background: var(--p-timeline-color-secondary-marker-background);
    border-color: var(--p-timeline-color-secondary-marker-border);
  }

  :global(.p-timeline-event-connector) {
    background: var(--p-timeline-color-secondary-connector-color);
  }
}

:global(.p-timeline-event-opposite) {
	flex: none;
}

:global(.p-timeline-event-connector) {
	&.inactive,
	&.isLastActive {
		background: var(--p-disabled-color);
	}
}

:global(.p-timeline-event-marker) {
	&.inactive {
		background: var(--p-disabled-color);
		border-color: var(--p-gray-scale-100);
	}
}
</style>
